import { Controller } from "@hotwired/stimulus";
import createNumberPipe from "../../utils/createNumberPipe";
import { ImaskedElement } from "../inputs/imask_controller";

export default class extends Controller {
  static targets = [
    "lineItem",
    "discount",
    "subtotal",
    "total",
    "currency",
    "taxes",
    "exchangeRate"
  ];

  static values = { currency: { type: String } };

  numberPipe;
  subtotal: number;
  currency: string | undefined;
  scale: number | undefined;
  padFractionalZeros: boolean | undefined;
  discount: number;
  taxes: number;
  mutationObserver: MutationObserver | undefined;

  declare readonly hasCurrencyTarget: boolean;
  declare readonly currencyTarget: HTMLInputElement;

  declare readonly hasTotalTarget: boolean;
  declare readonly totalTarget: HTMLDivElement;

  declare readonly hasSubtotalTarget: boolean;
  declare readonly subtotalTarget: HTMLDivElement;

  declare readonly hasDiscountTarget: boolean;
  declare readonly discountTarget: HTMLDivElement;

  declare readonly hasTaxesTarget: boolean;
  declare readonly taxesTarget: HTMLDivElement;

  declare readonly hasLineItemTarget: boolean;
  declare readonly hasLineItemTargets: boolean;
  declare readonly lineItemTargets: [HTMLInputElement];

  declare readonly hasCurrencyValue: boolean;
  declare readonly currencyValue: string;

  connect() {
    if (this.hasCurrencyTarget) {
      this.currency = this.currencyTarget.value;
    } else {
      this.currency = this.currencyValue;
    }
    this.padFractionalZeros = false;
    this.updateTotal();
  }

  updateTotal() {
    if (!this.numberPipe && this.lineItemTargets.length > 0) {
      const input: ImaskedElement<HTMLInputElement> | null =
        this.lineItemTargets[0].querySelector(
          'input[data-forms--document--line-item-target="price"]'
        );
      if (input) {
        this.padFractionalZeros =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (input.imaskOptions as any)?.blocks?.num?.padFractionalZeros ?? false;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.scale = (input.imaskOptions as any)?.blocks?.num?.scale ?? 4;
        this.createNumberPipe();
      }
    }
    this.subtotal = 0.0;
    this.discount = 0.0;
    this.taxes = 0.0;
    this.lineItemTargets.forEach((lineItem) => {
      this.subtotal += parseFloat(lineItem.dataset.subtotal ?? "0.0");
      this.discount += parseFloat(lineItem.dataset.discount ?? "0.0");
      this.taxes += parseFloat(lineItem.dataset.taxes ?? "0.0");
    });

    this.updateTotalLabels();
  }

  createNumberPipe() {
    if (!this.numberPipe && this.currency && this.scale) {
      this.numberPipe = createNumberPipe(
        this.currency,
        this.scale,
        this.padFractionalZeros
      );
    }
  }

  updateCurrency(e: InputEvent & { target: HTMLInputElement }) {
    this.currency = e.target.value;
    this.numberPipe = null;
    this.createNumberPipe();
    this.updateTotal();
    this.updateTotalLabels();

    this.dispatch("currencyChanged", {
      detail: { currency: this.currency }
    });
  }

  currencyTargetConnected() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    let previousValue: string;
    this.mutationObserver = new MutationObserver((mutations) => {
      if (
        mutations[0].attributeName == "value" &&
        previousValue != this.currencyTarget.value
      ) {
        previousValue = this.currencyTarget.value;
        this.currencyTarget.dispatchEvent(new Event("change"));
      }
    });

    this.mutationObserver.observe(this.currencyTarget, {
      attributes: true
    });
  }

  updateTotalLabels() {
    if (!this.numberPipe) {
      return;
    }

    if (this.hasDiscountTarget) {
      this.discountTarget.innerHTML = this.numberPipe(`${this.discount}`);
    }

    if (this.hasTaxesTarget) {
      this.taxesTarget.innerHTML = this.numberPipe(`${this.taxes}`);
    }

    if (this.hasSubtotalTarget) {
      this.subtotalTarget.innerHTML = this.numberPipe(
        `${this.subtotal - this.taxes}`
      );
    }
    this.totalTarget.innerHTML = this.numberPipe(
      `${this.subtotal - this.discount}`
    );
  }
}
